import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { NzTableComponent, NzTableModule } from 'ng-zorro-antd/table';
import { UILoading } from '../../loading';
import { GetFieldValuePipe } from '@shared/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UITableBodyColumn, UITableHeaderColumn } from '../components';
import {
  ITableActionsColumnReturnData,
  ITableColumnConfigType,
  ITableConfig,
  TableActionsColumnType,
} from '../index';
import { debounceTime, distinctUntilChanged } from 'rxjs';

@Component({
  selector: 'ui-table-infinite',
  templateUrl: './table-infinite.component.html',
  styleUrl: './table-infinite.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NzTableModule, UITableHeaderColumn, UITableBodyColumn, UILoading, GetFieldValuePipe],
  providers: [GetFieldValuePipe],
})
export class UITableInfinite implements AfterViewInit {
  @ViewChild('virtualTable', { static: false }) nzTableComponent?: NzTableComponent<any>;

  @Input() loading = true;
  @Input() config?: ITableConfig;
  @Input() data: any[] = [];
  @Input() total = 0;

  @Input() set selectedRows(rows: any[]) {
    this.setOfCheckedId = new Set(rows ?? []);
  }
  @Input() rowSelectionDisabled = false;

  @Input() scrollHeight = '100%';
  @Input() virtualItemSize = 54;
  @Input() infiniteScrollThreshold = 5;

  @Output() updateTableData = new EventEmitter<void>();
  @Output() rowSelected = new EventEmitter<any[]>();

  @Output() actionClicked = new EventEmitter<ITableActionsColumnReturnData>();
  @Output() loadMore = new EventEmitter<void>();

  private destroyRef = inject(DestroyRef);

  get isRowSelectionTable(): boolean {
    return this.config?.rowSelection?.type != null && this.config.rowSelection.type != 'none';
  }

  trackBy = (rowData: unknown, index?: number) => {
    if (this.config?.trackBy) {
      return this.config?.trackBy(rowData);
    }
    return index;
  };

  get rowSelectionByField(): string | undefined {
    return this.config?.rowSelection?.selectByField;
  }
  checked = false;
  indeterminate = false;

  setOfCheckedId = new Set<any>();

  private getFieldValuePipe = inject(GetFieldValuePipe);

  ngAfterViewInit(): void {
    this.setupInfiniteScroll();
  }

  private setupInfiniteScroll(): void {
    if (!this.nzTableComponent?.cdkVirtualScrollViewport) return;

    this.nzTableComponent.cdkVirtualScrollViewport.renderedRangeStream
      .pipe(
        debounceTime(100),
        distinctUntilChanged((prev, curr) => prev.end === curr.end),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(range => {
        this.checkAndLoadMore(range.end);
      });
  }

  private checkAndLoadMore(end: number): void {
    const totalItems = this.data.length;
    const threshold = totalItems - this.infiniteScrollThreshold;

    if (end >= threshold && !this.loading && this.total >= totalItems) {
      this.loadMore.emit();
    }
  }

  updateCheckedSet(id: any, checked: boolean): void {
    if (checked) {
      this.setOfCheckedId.add(id);
    } else {
      this.setOfCheckedId.delete(id);
    }
    this.rowSelected.emit(Array.from(this.setOfCheckedId));
  }

  onItemChecked(row: any, checked: boolean): void {
    this.updateCheckedSet(this.getFieldValuePipe.transform(row, this.rowSelectionByField), checked);
    this.refreshCheckedStatus();
  }

  refreshCheckedStatus(): void {
    this.checked = this.data?.every(row =>
      this.setOfCheckedId.has(this.getFieldValuePipe.transform(row, this.rowSelectionByField)),
    );
    this.indeterminate =
      this.data?.some(row =>
        this.setOfCheckedId.has(this.getFieldValuePipe.transform(row, this.rowSelectionByField)),
      ) && !this.checked;
  }

  onAllChecked(checked: boolean): void {
    this.data?.forEach(row =>
      this.updateCheckedSet(
        this.getFieldValuePipe.transform(row, this.rowSelectionByField),
        checked,
      ),
    );
    this.refreshCheckedStatus();
  }

  get tableColumns(): ITableColumnConfigType[] {
    return (this.config?.columns ?? []).filter(column => !column.isHidden);
  }

  onUpdateTableData(): void {
    this.updateTableData.emit();
  }

  onRowClicked(action: TableActionsColumnType, rowData: any): void {
    this.actionClicked.emit({
      type: action,
      rowData: rowData,
    });
  }
}
